import { ChangeDetectorRef, Component, Inject, Injector, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LanguageEnum } from 'src/app/core/common/enum';
import { LanguageService } from 'src/app/core/helper';
import {
  BlocState,
  ErrorState,
  ProcessingState,
  ShowLoadingComponentState,
  ShowSuccessMessageState,
} from '../../bloc/base-bloc';
import { SectionStateStatus } from '../../common/shared.enums';
import { destroyStream, LoadingService } from '../../helper';
import { AsyncFeedbackService } from '../../services/async-feedback.service';
import { FeedbackModel, FeedbackType, NonDrawing } from '../../model';
import { UserPermissionService } from 'src/app/core/services/user-permission-service';
import { Permissions } from 'src/app/core/common';
import { ThemeColors } from '../../common';
import { ThemesService } from '../../../core/services/themes.service';

@Component({
  standalone: true,
  selector: 'app-base-component',
  template: ``,
})
export class BaseComponent implements OnDestroy {
  protected destroy$: Subject<boolean> = new Subject<boolean>();
  protected translationsList: any = {};
  protected translationService: TranslateService;
  public sectionState: SectionStateStatus = SectionStateStatus.Ready;
  public language: LanguageEnum;
  public currentThemeColors: ThemeColors;
  public isLoading: BehaviorSubject<boolean> = new BehaviorSubject(false);
  constructor(
    @Inject(String) protected translationArr: string[] = [],
    protected injector: Injector = null,
    @Inject(Boolean) public getThemeColors?: boolean
  ) {
    if (injector) {
      this.translationService = injector.get<TranslateService>(TranslateService);
      this.language = this.translationService.currentLang as LanguageEnum;
      if (this.translationArr && this.translationArr.length > 0) {
        this.translationService
          .get([...this.translationArr])
          .pipe(takeUntil(this.destroy$))
          .subscribe((translations: any) => {
            this.translationsList = translations;
          });
        this.translationService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe((lang: LangChangeEvent) => {
          this.translationService
            .get([...this.translationArr])
            .pipe(takeUntil(this.destroy$))
            .subscribe((translations: any) => {
              this.translationsList = translations;
            });
          this.language = lang.lang as LanguageEnum;
        });
      }
      if (getThemeColors) {
        this.currentThemeColors = this.injector.get(ThemesService).currentThemeColors
        this.injector
          .get(ThemesService).changeTheme$.pipe(takeUntil(this.destroy$)).subscribe(theme=>{
            this.currentThemeColors = theme;
          })
      }
    }
  }

  ngOnDestroy(): void {
    destroyStream(this.destroy$);
  }

  protected onHandleBlocState(state: BlocState): void {
    // inject loading service
    const loadingService = this.injector.get(LoadingService);
    // no state
    if (!state) {
      return;
    }
    // stop loading
    loadingService.stopLoading();
    this.isLoading.next(false);
    // run loading
    if (state instanceof ProcessingState) {
      loadingService.startLoading(state.drawingType);
    }
    // error state
    else if (state instanceof ErrorState) {
      this.onHandleErrorMessage(state);
    } else if (state instanceof ShowSuccessMessageState) {
      this.onHandleSuccessMessages(state);
    } else if (state instanceof ShowLoadingComponentState) {
      this.isLoading.next(true);
    }
  }

  protected showSnackBar(feedBack: FeedbackModel): void {
    if (this.injector) {
      const feedbackService = this.injector.get(AsyncFeedbackService);
      feedbackService.showFeedback(feedBack);
    }
  }

  protected checkCurrentLanguageAndChangeIt(language?: LanguageEnum, hardReload: boolean = true): void {
    if (this.injector) {
      const languageService = this.injector.get(LanguageService);
      if (language) {
        languageService.changeLanguage(language, hardReload);
      } else {
        if (languageService.currentLanguage$.value.isoCode == LanguageEnum.English) {
          languageService.changeLanguage(LanguageEnum.Arabic, hardReload);
        } else {
          languageService.changeLanguage(LanguageEnum.English, hardReload);
        }
      }
      this.language = languageService.currentLanguage$.value.isoCode;
      this.injector.get(ChangeDetectorRef).detectChanges();
    }
  }

  protected navigateToRoute(route: string, queryParams?: object): void {
    if (this.injector) {
      const router = this.injector.get(Router);
      router.navigate([route], { queryParams: queryParams });
    }
  }

  protected hasUserPermission(permission: Permissions): boolean {
    return this.injector.get(UserPermissionService).isHasPermission([permission]);
  }

  // privates
  private getDefaultTranslations(messageKey: string): void {
    if (!this.translationArr.includes(messageKey)) {
      this.translationService
        .get([...this.translationArr, messageKey])
        .pipe(takeUntil(this.destroy$))
        .subscribe((translations) => (this.translationsList = translations));
    }
  }

  private onHandleErrorMessage(state: ErrorState<NonDrawing>): void {
    if (state.error.errors['message']) {
      this.showSnackBar(new FeedbackModel(FeedbackType.Failure, state.error.errors['message']));
    } else {
      this.getDefaultTranslations('ERROR_MESSAGES');
      this.showSnackBar(
        new FeedbackModel(FeedbackType.Failure, this.translationsList['ERROR_MESSAGES']['ERROR_HAPPENED'])
      );
    }
  }

  private onHandleSuccessMessages(state: ShowSuccessMessageState<unknown>): void {
    this.getDefaultTranslations('SUCCESS_MESSAGES');
    state.customMessage
      ? this.showSnackBar(
          new FeedbackModel(
            FeedbackType.Success,
            `${this.translationsList['SUCCESS_MESSAGES'][state.messageTranslationKey]}` + state.customMessage
          )
        )
      : this.showSnackBar(
          new FeedbackModel(
            FeedbackType.Success,
            `${this.translationsList['SUCCESS_MESSAGES'][state.messageTranslationKey]}`
          )
        );
  }
}
